import type { FC } from 'react';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaSortAmountDown as BottomIcon,
  FaSortAmountUp as TopIcon,
} from 'react-icons/fa';
import {
  Box,
  Grid,
  Skeleton,
  Stack,
  SvgIcon,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSetAtom } from 'jotai';

import type {
  DashboardWidgetConfig,
  DashboardWidgetProps,
  UpstreamApiDashboardResult,
} from 'src/types/dashboard';
import useGuidedTour from 'src/components/app/context/GuidedTourContext';
import { chatbotPrimeTexts } from 'src/components/chatbot/atoms';
import { useStatistics } from 'src/components/dashboard/DashboardContext/StatisticsContext';
import ErrorOverlayWithRefetchButton from 'src/components/dashboard/DashboardPage/components/ErrorOverlayWithRefetchButton';
import NumberDifference from 'src/components/dashboard/DashboardPage/components/NumberDifference';
import { getAggregateByQuestion } from 'src/components/dashboard/helper';
import useWidgetChartConfig from 'src/components/dashboard/hooks/useWidgetChartConfig';

import { styles } from './index.styles';

export const TABLE_WIDGET_TEST_ID = 'table-widget';

export interface QuestionRow {
  diff: number;
  fav: number;
  label: string;
}

export const getQuestionRows = (
  config: DashboardWidgetConfig,
  focal_result?: UpstreamApiDashboardResult,
) =>
  config.questions
    .map((question) => {
      const aggregate = getAggregateByQuestion(question, focal_result);

      if (!aggregate) return undefined;

      return {
        diff: aggregate.ptop2_yoyDiff,
        fav: aggregate.ptop2,
        label: aggregate.label,
      };
    })
    .filter((question) => !!question) as QuestionRow[];

const TableWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const { t } = useTranslation('dashboard');
  const { data, loading, errorMsg, refetch } = useStatistics();
  const theme = useTheme();
  const upMd = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const chartConfig = useWidgetChartConfig(config);
  const setChatbotPrimeTexts = useSetAtom(chatbotPrimeTexts('preChat'));
  const questionRows = useMemo(
    () => getQuestionRows(config, data?.focal_result),
    [config, data],
  );
  const { createStep } = useGuidedTour();
  const guidedTour = useMemo(
    () => createStep(config.id, config.guided_tour_step),
    [config.guided_tour_step],
  );

  useEffect(() => {
    if (config.name.includes('Top 10')) {
      let primeText = `${config.name} ${t('Labels.FavPercentage')} ${t(
        'Labels.DiffToLastYear',
      )} \n`;
      questionRows.forEach(
        (item) => (primeText += `${item.label} ${item.fav} ${item.diff} \n`),
      );
      setChatbotPrimeTexts({
        tableWidget: {
          primeQuestion: 'What else is important?',
          primeText: primeText,
        },
      });
    }
  }, [config, questionRows]);

  if (loading || errorMsg) {
    return (
      <Box sx={{ position: 'relative' }}>
        <Stack
          direction="column"
          spacing={2}
          data-test-id="loading-table"
          sx={{ p: 2, position: 'relative' }}
        >
          <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
            {config.name}
          </Typography>
          <Skeleton variant="rectangular" height={40} />
          <Stack direction="column" spacing={1}>
            {config.questions.map((_, i) => (
              <Skeleton variant="rectangular" height={60} key={i} />
            ))}
          </Stack>
        </Stack>
        {errorMsg && (
          <ErrorOverlayWithRefetchButton
            errorMsg={errorMsg}
            refetch={refetch}
            name={config.name}
          />
        )}
      </Box>
    );
  }

  // Do not render this widget, if the dataset is empty after calculating
  if (!Array.isArray(questionRows) || !questionRows.length) {
    return null;
  }

  const listType =
    chartConfig.listType === 'top' || chartConfig.listType === 'bottom'
      ? chartConfig.listType
      : undefined;

  return (
    <Box sx={{ p: 2, flexGrow: 1, maxWidth: '100%', position: 'relative' }}>
      {!!guidedTour && (
        <Box sx={{ position: 'absolute', top: 0, left: 0 }}>{guidedTour}</Box>
      )}
      <Grid
        className="TableWidget-root"
        container
        data-test-id={TABLE_WIDGET_TEST_ID}
        spacing={2}
        sx={styles.tableWidget}
      >
        <Grid
          className="TableWidget-tableHead"
          container
          item
          spacing={2}
          sx={styles.tableHead}
        >
          {/* Column 1 */}
          <Grid item md={true}>
            <Typography
              className="TableWidget-heading"
              component="span"
              sx={[
                styles.heading,
                listType === 'top' && styles.headingTopStatements,
                listType === 'bottom' && styles.headingBottomStatements,
              ]}
            >
              {listType === 'top' && (
                <SvgIcon sx={styles.headingIcon}>
                  <TopIcon />
                </SvgIcon>
              )}
              {listType === 'bottom' && (
                <SvgIcon sx={styles.headingIcon}>
                  <BottomIcon />
                </SvgIcon>
              )}
              {config.name}
            </Typography>
          </Grid>
          {/* Column 2 */}
          {upMd && (
            <Grid item md={2} sx={styles.favDivLabelWrapper}>
              <Typography sx={{ fontWeight: 'bold' }}>
                {t('Labels.FavPercentage')}
              </Typography>
            </Grid>
          )}
          {/* Column 3 */}
          {upMd && (
            <Grid item md={2} sx={styles.favDivLabelWrapper}>
              <Typography sx={{ fontWeight: 'bold' }}>
                {t('Labels.DiffToLastYear')}
              </Typography>
            </Grid>
          )}
        </Grid>

        {questionRows.map((questionRow, index) => (
          <Grid
            className="TableWidget-tableBody"
            container
            item
            key={index}
            spacing={2}
            sx={styles.tableBody}
          >
            {/* Column 1 */}
            <Grid item xs={12} md={true}>
              <Typography component="span" sx={{ display: 'flex' }}>
                <Typography component="span" sx={styles.rank}>
                  {index + 1}.
                </Typography>
                {questionRow.label}
              </Typography>
            </Grid>

            {/* Column 2 */}
            <Grid
              item
              md={2}
              sx={[styles.favDivLabelWrapper, styles.favDivLabelWrapperOffset]}
            >
              {!upMd && (
                <Typography component="span" sx={{ fontWeight: 'bold' }}>
                  {t('Labels.FavPercentage')}:
                </Typography>
              )}

              <Typography component="span">{questionRow.fav}</Typography>
            </Grid>

            {/* Column 3 */}
            <Grid
              item
              md={2}
              sx={[{ ml: { sm: 8, md: 0 } }, styles.favDivLabelWrapper]}
            >
              {!upMd && (
                <Typography component="span" sx={{ fontWeight: 'bold' }}>
                  {t('Labels.DiffToLastYear')}:
                </Typography>
              )}

              <NumberDifference comparisonDiff={questionRow.diff} showArrow />
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default TableWidget;
